export enum ABTestStatus {
  DRAFT = 'draft',
  RUNNING = 'running',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export type ABMetric =
  | 'ctr'
  | 'delivery_rate'
  | 'registration'
  | 'deposit'
  | 'conversion';

export const AB_METRIC_LABELS: Record<ABMetric, string> = {
  ctr: 'CTR (клики)',
  delivery_rate: 'Доставляемость',
  registration: 'Регистрации',
  deposit: 'Депозиты',
  conversion: 'Конверсия (рег + деп)',
};

export interface ABVariantBrief {
  id: number;
  name: string;
  is_control: boolean;
  traffic_percentage: number;
  sent: number;
  clicked: number;
  ctr: number;
}

export interface ABTestListItem {
  id: number;
  app_id: number;
  name: string;
  description: string;
  status: ABTestStatus;
  segment_id: number | null;
  segment_name: string;
  locale: string;
  os: string;
  offer: string;
  has_registration: boolean | null;
  has_deposit: boolean | null;
  start_date: string;
  end_date: string | null;
  winner_variant_id: number | null;
  winner_name: string;
  winning_metric: string;
  confidence_level: number;
  variants: ABVariantBrief[];
  total_sent: number;
  total_clicked: number;
  ctr: number;
  created_at: string;
}

export interface ABTestListResponse {
  tests: ABTestListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ABVariantReport {
  id: number;
  name: string;
  is_control: boolean;
  traffic_percentage: number;
  title: string;
  body: string;
  image_url: string;
  click_url: string;
  sent: number;
  delivered: number;
  clicked: number;
  failed: number;
  registrations: number;
  deposits: number;
  delivery_rate: number;
  ctr: number;
  registration_rate: number;
  deposit_rate: number;
  conversion_rate: number;
  base: number;
  conversions: number;
  rate: number;
  ci_low: number;
  ci_high: number;
  uplift: number | null;
  p_value: number | null;
  confidence: number | null;
  significant: boolean;
  is_winner: boolean;
}

export interface ABMetricOption {
  value: ABMetric;
  label: string;
}

export interface ABTestReport {
  test_id: number;
  name: string;
  description: string;
  status: ABTestStatus;
  app_id: number;
  segment_id: number | null;
  segment_name: string;
  metric: ABMetric;
  metric_title: string;
  base: string;
  start_date: string;
  end_date: string | null;
  duration_hours: number;
  duration_label: string;
  total_sent: number;
  total_delivered: number;
  total_clicked: number;
  total_failed: number;
  total_registrations: number;
  total_deposits: number;
  recipients: number;
  variants: ABVariantReport[];
  winner: ABVariantReport | null;
  is_significant: boolean;
  confidence_level: number;
  p_value: number;
  alpha_adjusted: number;
  required_per_variant: number;
  progress: number;
  recommendation: string;
  warnings: string[];
  winner_variant_id: number | null;
  winning_metric: string;
  available_metrics: ABMetricOption[];
}

export type RecipientStatus = '' | 'delivered' | 'clicked' | 'failed' | 'converted';

export interface ABTestRecipient {
  variant_id: number;
  variant_name: string;
  is_control: boolean;
  app_user_id: number;
  af_id: string;
  external_id: string;
  os: string;
  locale: string;
  country: string;
  offer: string;
  has_registration: boolean;
  has_deposit: boolean;
  message_id: string;
  sent_at: string;
  delivered_at: string | null;
  clicked_at: string | null;
  failed_at: string | null;
  attributed_registration: boolean;
  attributed_deposit: boolean;
}

export interface ABTestRecipientsResponse {
  recipients: ABTestRecipient[];
  total: number;
  limit: number;
  offset: number;
}

export interface RecipientQuery {
  variant_id?: number;
  status?: RecipientStatus;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ABTestSendJob {
  test_id: number;
  status: 'running' | 'completed' | 'failed';
  scanned: number;
  queued: number;
  skipped: number;
  failed: number;
  per_variant: Record<string, number>;
  started_at: string;
  finished_at: string | null;
  error?: string;
}

export interface ABAudienceShare {
  name: string;
  traffic_percentage: number;
  estimate: number;
}

export interface ABAudiencePreview {
  total: number;
  already_sent: number;
  remaining: number;
  per_variant: ABAudienceShare[];
}

export interface ABTimePoint {
  variant_id: number;
  bucket: string;
  sent: number;
  delivered: number;
  clicked: number;
  failed: number;
}

export interface ABTestVariant {
  id: number;
  ab_test_id: number;
  name: string;
  is_control: boolean;
  traffic_percentage: number;
  title: string;
  body: string;
  image_url: string;
  click_url: string;
  custom_data?: Record<string, any>;
  created_at: string;
  stats?: ABTestVariantStats;
}

export interface ABTestVariantStats {
  id: number;
  variant_id: number;
  sent_count: number;
  delivered_count: number;
  clicked_count: number;
  failed_count: number;
  registrations: number;
  deposits: number;
  updated_at: string;
  ctr: number;
  delivery_rate: number;
  conversion_rate: number;
  ctr_delivered: number;
}

export interface ABTest {
  id: number;
  app_id: number;
  name: string;
  description: string;
  status: ABTestStatus;
  segment_id: number | null;
  locale: string;
  has_registration?: boolean;
  has_deposit?: boolean;
  os: string;
  offer: string;
  start_date: string;
  end_date?: string;
  winner_variant_id: number | null;
  winning_metric: string;
  confidence_level: number;
  p_value: number | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  variants?: ABTestVariant[];
}

export interface ABTestVariantCreateRequest {
  name: string;
  is_control: boolean;
  traffic_percentage: number;
  title: string;
  body: string;
  image_url?: string;
  click_url?: string;
  custom_data?: Record<string, any>;
}

export interface ABTestCreateRequest {
  app_id: number;
  name: string;
  description?: string;
  segment_id?: number;
  locale?: string;
  has_registration?: boolean;
  has_deposit?: boolean;
  os?: string;
  offer?: string;
  start_date: string;
  end_date?: string;
  variants: ABTestVariantCreateRequest[];
}

export type ABTestUpdateRequest = ABTestCreateRequest;

export interface ABAudiencePreviewRequest {
  app_id: number;
  segment_id?: number;
  locale?: string;
  has_registration?: boolean;
  has_deposit?: boolean;
  os?: string;
  offer?: string;
  variants: Pick<ABTestVariantCreateRequest, 'name' | 'traffic_percentage'>[];
}